"use client";

import { useEffect, useState } from "react";

const STORAGE_KEY = "ellaandil:recently-viewed";
const MAX_RECENT = 8;

const readStored = (): string[] => {
	if (typeof window === "undefined") return [];
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) return [];
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
	} catch {
		return [];
	}
};

const writeStored = (values: string[]): void => {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
	} catch {
		// quota / privacy mode — silently drop
	}
};

/**
 * Record a Centra product id as viewed — most-recent-first, deduped, capped
 * at `MAX_RECENT`. Fire-and-forget: the writer (PDP) never needs the list
 * back, so this stays a plain function rather than a hook — no re-render on
 * every view.
 *
 * Store the bare product id only, never the fetched `ProductCard` — price,
 * stock and currency are market/pricelist-scoped, so a cached card would
 * silently go stale across a market switch or a price change. Fallback
 * consumers re-fetch fresh data for these ids instead (see
 * `products.exploration`).
 */
export const recordRecentlyViewed = (id: string): void => {
	if (!id) return;
	const next = [id, ...readStored().filter((existing) => existing !== id)].slice(0, MAX_RECENT);
	writeStored(next);
};

/**
 * The locally tracked recently-viewed product ids, most-recent-first.
 * Hydrates in an effect (not at init) to stay SSR-safe and avoid a hydration
 * mismatch — `readStored()` before mount would read `[]` on the server and
 * whatever's in the visitor's browser on the client.
 */
export const useRecentlyViewedIds = (): string[] => {
	const [ids, setIds] = useState<string[]>([]);

	useEffect(() => {
		setIds(readStored());
	}, []);

	return ids;
};
